import {
  skyTerrainSample,
  skyTerrainHeight,
} from '../../../packages/domain/src/87-sky-terrain.mjs';
import { SKY_WORLD } from '../../../packages/domain/src/86-sky-world-scale.mjs';

/** Original heavy bonus creatures share imported geometry/materials. Their
 * anatomical pivots are authored in Blender; no disposable geometry is created. */
export function createSkyMegafauna(T, clone, item, resources = new Set()) {
  const group = clone(item.bonusForm === 'mechwalker' ? 'mechwalker' : 'mammoth');
  const model = group.children[0];
  model.position.y = SKY_WORLD.groundY;
  group.userData.megafaunaModel = model;
  group.userData.heavyParts = [];
  group.userData.heavyFeet = [];
  group.userData.guardianMaterials = [];
  const rewardColors = {
    rapid: '#79f3c0',
    power: '#ffc56a',
    shield: '#7bcfff',
    boost: '#b99cff',
    focus: '#ffa1d3',
  };
  const cellMaterials = new Map();
  group.updateMatrixWorld(true);
  model.traverse((node) => {
    if (node.isMesh) {
      if (node.material.name === 'reward cell warm gold') {
        if (!cellMaterials.has(node.material)) {
          const material = node.material.clone();
          material.color.set(rewardColors[item.bonusKind] ?? '#ffc56a');
          material.emissive.copy(material.color);
          resources.add(material);
          group.userData.guardianMaterials.push(material);
          cellMaterials.set(node.material, material);
        }
        node.material = cellMaterials.get(node.material);
      }
      return;
    }
    if (/^(body|head|neck|leg_|knee_|foot_|trunk_|ear_|tail_|utility_arm)/.test(node.name)) {
      group.userData.heavyParts.push({
        node,
        position: node.position.clone(),
        rotation: node.rotation.clone(),
        quaternion: node.quaternion.clone(),
        scale: node.scale.clone(),
      });
    }
    if (node.name.startsWith('foot_')) {
      const marker = node.userData.skySole;
      let sole;
      if (Array.isArray(marker) && marker.length === 3 && marker.every(Number.isFinite)) {
        // Skinned meshes live beside their bones, so descendant mesh bounds
        // cannot describe a foot. Exported markers stay in that bone's space.
        sole = new T.Vector3().fromArray(marker);
      } else {
        const bounds = new T.Box3().setFromObject(node);
        if (bounds.isEmpty()) return;
        sole = bounds.getCenter(new T.Vector3());
        sole.y = bounds.min.y;
        node.worldToLocal(sole);
      }
      group.userData.heavyFeet.push({ node, sole, world: new T.Vector3() });
    }
  });
  return group;
}

const smooth = (value) => value * value * (3 - 2 * value);

/** Four-beat, long-stance gait with delayed head/trunk response. All movement
 * uses simulation age, so pause and rewind restore exactly. The lowest authored
 * sole stays grounded; body weight never turns into the former floating shuffle. */
export function animateSkyMegafauna(group, item, reduced) {
  const time = reduced ? 0 : (item.age ?? 0);
  const model = group.userData.megafaunaModel;
  model.position.y = SKY_WORLD.groundY;
  const machine = item.bonusForm === 'mechwalker';
  const period = machine ? 2.8 : 3.2;
  const stance = machine ? 0.76 : 0.68;
  const impactAge = (item.age ?? 0) - (item.impactAt ?? -10);
  const flinch =
    !reduced && impactAge >= 0 && impactAge < 0.42
      ? Math.sin((Math.PI * impactAge) / 0.42) * Math.exp(-impactAge * 5)
      : 0;
  for (const material of group.userData.guardianMaterials ?? [])
    material.emissiveIntensity = reduced ? 0.65 : 0.65 + flinch * 0.7;
  const groundedTerrain = Number.isFinite(item.terrainTravel);
  const surface = groundedTerrain ? skyTerrainSample(item.x, item.z, item.terrainTravel) : null;
  group.rotation.set(
    surface ? -Math.atan(surface.dz) : 0,
    reduced ? 0 : Math.atan2(item.vx ?? 0, 1.5) * 0.6,
    0,
  );
  for (const part of group.userData.heavyParts) {
    const node = part.node;
    node.position.copy(part.position);
    node.quaternion.copy(part.quaternion);
    node.scale.copy(part.scale);
    if (reduced) continue;
    const name = node.name;
    const side = name.includes('left') ? -1 : 1;
    const offset = name.includes('front') ? (side < 0 ? 0 : 0.5) : side < 0 ? 0.75 : 0.25;
    const cycle = (((time / period + offset) % 1) + 1) % 1;
    const swinging = cycle > stance;
    const swing = swinging ? (cycle - stance) / (1 - stance) : 0;
    // Authored animals face +Z: stance sweeps the sole backward under the body.
    const hip = swinging ? 0.24 - smooth(swing) * 0.48 : -0.24 + (cycle / stance) * 0.48;
    const knee = Math.sin(swing * Math.PI) * 0.46;
    if (name.startsWith('leg_')) node.rotation.x += hip;
    else if (name.startsWith('knee_')) node.rotation.x += knee;
    else if (name.startsWith('foot_')) node.rotation.x -= hip + knee;
    else if (name === 'body') {
      node.rotation.z += Math.sin((time * Math.PI * 2) / period) * (machine ? 0.022 : 0.04);
      node.rotation.x -= flinch * 0.028;
      if (!machine) node.scale.x *= 1 + Math.sin(time * 1.5) * 0.012;
      node.rotation.x += Math.sin((time * Math.PI) / 1.6 - 0.45) * 0.025;
    } else if (name === 'head') {
      node.rotation.y +=
        Math.sin(time * (machine ? 0.9 : 0.65) - 0.5) * (machine ? 0.18 : 0.09) +
        (item.vx ?? 0) * 0.12;
      node.rotation.x += Math.sin(time * 1.7 - 0.8) * (machine ? 0.025 : 0.065) - flinch * 0.1;
    } else if (name === 'neck' && machine) {
      node.rotation.y += Math.sin(time * 0.9 - 1.1) * 0.075;
      node.rotation.x += Math.sin(time * 0.7 - 0.4) * 0.035 - flinch * 0.04;
    } else if (name.startsWith('trunk_')) {
      const segment = Number(name.match(/\d+/)?.[0] ?? 0);
      node.rotation.z += Math.sin(time * 1.1 - segment * 0.6) * 0.09;
      node.rotation.x += Math.sin(time * 0.75 - segment * 0.7) * 0.12 - flinch * 0.07;
    } else if (name.startsWith('tail_')) {
      const segment = Number(name.match(/\d+/)?.[0] ?? 0);
      node.rotation.y += Math.sin(time * 0.7 - segment * 0.55) * 0.08;
      node.rotation.x += Math.sin(time * 1.1 - segment * 0.4) * 0.035;
    } else if (name.startsWith('ear_')) {
      node.rotation.y += side * (Math.sin(time * 0.7) * 0.1 + flinch * 0.18);
    } else if (name.startsWith('utility_arm')) {
      node.rotation.z += Math.sin(time * 0.9 - 0.6) * 0.065;
      node.rotation.x -= Math.sin(time * 0.9 - 1.1) * 0.035;
    }
  }
  group.updateMatrixWorld(true);
  let floor = Infinity;
  for (const foot of group.userData.heavyFeet) {
    foot.world.copy(foot.sole).applyMatrix4(foot.node.matrixWorld);
    const height = groundedTerrain
      ? skyTerrainHeight(foot.world.x, foot.world.z, item.terrainTravel)
      : SKY_WORLD.groundY;
    floor = Math.min(floor, foot.world.y - height);
  }
  if (Number.isFinite(floor)) model.position.y -= floor / Math.cos(group.rotation.x);
  group.updateMatrixWorld(true);
  for (const skeleton of group.userData.megafaunaSkeletons ?? []) skeleton.update();
}
